import ReactGA from 'react-ga4'

const QUOTE_CONTEXT_STORAGE_KEY = 'ga_quote_context_v1'
const MAX_STRING_LENGTH = 100
const MAX_STORED_QUOTE_CONTEXTS = 20

type JourneyLeg = 'outbound' | 'inbound'

type QuoteOptionContext = {
  supplierQuoteReference?: string
  vehicleTypeName?: string
  supplierName?: string
  priceTotal?: number
  currency?: string
  etaMinutes?: number
  isPreferredSupplier?: boolean
  isDestinationLogicQuote?: boolean
}

type QuoteContext = {
  journeyLeg: JourneyLeg
  options: QuoteOptionContext[]
}

type QuoteContextMap = Record<string, QuoteContext>

type BookingSelection = {
  journeyLeg: JourneyLeg
  quoteReference?: string
  supplierQuoteReference?: string
  quantity: number
}

type AnalyticsStateGetter = () => any

let analyticsStateGetter: AnalyticsStateGetter | undefined

const isGaEnabled = () => Boolean(process.env.REACT_APP_BASE_GA_KEY)

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'

const sanitizeString = (value: unknown, maxLength = MAX_STRING_LENGTH) => {
  if (value === undefined || value === null) {
    return undefined
  }

  const normalized = String(value).trim()
  if (!normalized) {
    return undefined
  }

  return normalized.slice(0, maxLength)
}

const sanitizeNumber = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined
  }

  return Number(value.toFixed(2))
}

const sanitizeBoolean = (value: unknown) => {
  if (typeof value !== 'boolean') {
    return undefined
  }

  return value
}

const sanitizeParamValue = (value: unknown) => {
  const stringValue = sanitizeString(value)
  if (stringValue !== undefined && typeof value !== 'number') {
    return stringValue
  }

  const numberValue = sanitizeNumber(value)
  if (numberValue !== undefined) {
    return numberValue
  }

  return sanitizeBoolean(value)
}

const sanitizeParams = (params: Record<string, unknown>) =>
  Object.entries(params).reduce<Record<string, string | number | boolean>>(
    (acc, [key, value]) => {
      const normalizedValue = sanitizeParamValue(value)
      if (normalizedValue !== undefined) {
        acc[key] = normalizedValue
      }
      return acc
    },
    {},
  )

const getRoleValue = (roles: unknown) => {
  if (!Array.isArray(roles)) {
    return undefined
  }

  const uniqueRoles = [
    ...new Set(roles.map((role) => sanitizeString(role, 40))),
  ]
    .filter(Boolean)
    .join('|')

  return sanitizeString(uniqueRoles)
}

const getApplicationThemeValue = (state: any) =>
  sanitizeString(
    state?.theme?.activeTheme || state?.clients?.client?.applicationTheme?.name,
  )

export const registerAnalyticsStateGetter = (getter: AnalyticsStateGetter) => {
  analyticsStateGetter = getter
}

const getAnalyticsState = () => {
  if (!analyticsStateGetter) {
    return undefined
  }

  try {
    return analyticsStateGetter()
  } catch (error) {
    return undefined
  }
}

const getAnalyticsContext = () => {
  const state = getAnalyticsState()

  return sanitizeParams({
    user_role: getRoleValue(state?.users?.user?.roles),
    application_theme: getApplicationThemeValue(state),
  })
}

export function syncAnalyticsUserPropertiesFromStore() {
  if (!isGaEnabled()) {
    return
  }

  const userProperties = getAnalyticsContext()
  if (Object.keys(userProperties).length === 0) {
    return
  }

  ReactGA.gtag('set', 'user_properties', userProperties)
}

const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (!isGaEnabled()) {
    return
  }

  syncAnalyticsUserPropertiesFromStore()
  ReactGA.event(name, sanitizeParams({ ...getAnalyticsContext(), ...params }))
}

const readStoredQuoteContexts = (): QuoteContextMap => {
  if (!canUseSessionStorage()) {
    return {}
  }

  try {
    const storedValue = window.sessionStorage.getItem(QUOTE_CONTEXT_STORAGE_KEY)
    return storedValue ? JSON.parse(storedValue) : {}
  } catch (error) {
    return {}
  }
}

const writeStoredQuoteContexts = (contexts: QuoteContextMap) => {
  if (!canUseSessionStorage()) {
    return
  }

  try {
    const trimmedEntries = Object.entries(contexts).slice(
      -MAX_STORED_QUOTE_CONTEXTS,
    )
    window.sessionStorage.setItem(
      QUOTE_CONTEXT_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(trimmedEntries)),
    )
  } catch (error) {
    // Ignore storage failures.
  }
}

const saveQuoteContext = (quoteReference: string, context: QuoteContext) => {
  const normalizedQuoteReference = sanitizeString(quoteReference)
  if (!normalizedQuoteReference) {
    return
  }

  const existingContexts = readStoredQuoteContexts()
  writeStoredQuoteContexts({
    ...existingContexts,
    [normalizedQuoteReference]: context,
  })
}

const normalizeQuoteOption = (vehicle: any): QuoteOptionContext => ({
  supplierQuoteReference: sanitizeString(vehicle?.supplierQuoteReference),
  vehicleTypeName: sanitizeString(
    vehicle?.vehicleDetails?.vehicleTypeName || vehicle?.vehicleTypeName,
  ),
  supplierName: sanitizeString(vehicle?.supplierName),
  priceTotal: sanitizeNumber(vehicle?.estimatedTotal?.total),
  currency: sanitizeString(vehicle?.estimatedTotal?.currency, 12),
  etaMinutes: sanitizeNumber(vehicle?.estimatedArrivalTime),
  isPreferredSupplier: sanitizeBoolean(vehicle?.isPreferredSupplier),
  isDestinationLogicQuote: sanitizeBoolean(vehicle?.isDestinationLogicQuote),
})

const normalizeQuoteOptions = (vehicles: any): QuoteOptionContext[] => {
  if (!Array.isArray(vehicles)) {
    return []
  }

  return vehicles
    .map((vehicle) => normalizeQuoteOption(vehicle))
    .filter((vehicle) => vehicle.supplierQuoteReference)
}

const trackQuoteResultsForLeg = (
  journeyLeg: JourneyLeg,
  quoteLeg: any,
  quoteRequest?: any,
) => {
  const quoteReference = sanitizeString(quoteLeg?.quoteReference)
  const options = normalizeQuoteOptions(quoteLeg?.vehicles)

  if (!quoteReference || options.length === 0) {
    return
  }

  saveQuoteContext(quoteReference, { journeyLeg, options })

  const vehicleTypes = sanitizeString(
    [
      ...new Set(
        options.map((option) => option.vehicleTypeName).filter(Boolean),
      ),
    ].join('|'),
  )

  trackEvent('quote_results_viewed', {
    quote_reference: quoteReference,
    journey_leg: journeyLeg,
    journey_type: quoteRequest?.journeyTypeName,
    passenger_count: quoteRequest?.numberOfPassengers,
    quote_count: options.length,
    vehicle_types: vehicleTypes,
    immediate_journey: quoteRequest?.isImmediateJourney,
  })

  options.forEach((option, index) => {
    trackEvent('quote_option_presented', {
      quote_reference: quoteReference,
      journey_leg: journeyLeg,
      option_index: index + 1,
      supplier_quote_reference: option.supplierQuoteReference,
      vehicle_type: option.vehicleTypeName,
      supplier_name: option.supplierName,
      price_total: option.priceTotal,
      currency: option.currency,
      eta_minutes: option.etaMinutes,
      preferred_supplier: option.isPreferredSupplier,
      destination_logic: option.isDestinationLogicQuote,
    })
  })
}

const getBookingSelections = (quotedBooking: any): BookingSelection[] => {
  const selections: BookingSelection[] = []

  ;(['outbound', 'inbound'] as JourneyLeg[]).forEach((journeyLeg) => {
    const leg = quotedBooking?.[journeyLeg]
    const quoteReference = sanitizeString(leg?.quoteReference)

    if (!quoteReference || !Array.isArray(leg?.vehicles)) {
      return
    }

    leg.vehicles.forEach((vehicle: any) => {
      const supplierQuoteReference = sanitizeString(
        vehicle?.supplierQuoteReference,
      )

      if (!supplierQuoteReference) {
        return
      }

      selections.push({
        journeyLeg,
        quoteReference,
        supplierQuoteReference,
        quantity:
          typeof vehicle?.quantity === 'number' && vehicle.quantity > 0
            ? vehicle.quantity
            : 1,
      })
    })
  })

  return selections
}

const findStoredQuoteOption = (
  quoteReference?: string,
  supplierQuoteReference?: string,
) => {
  if (!quoteReference || !supplierQuoteReference) {
    return undefined
  }

  const contexts = readStoredQuoteContexts()
  return contexts[quoteReference]?.options?.find(
    (option) => option.supplierQuoteReference === supplierQuoteReference,
  )
}

export const trackQuoteResultsViewed = (
  quoteResponseData: any,
  quoteRequest?: any,
) => {
  trackQuoteResultsForLeg('outbound', quoteResponseData?.outbound, quoteRequest)
  trackQuoteResultsForLeg('inbound', quoteResponseData?.inbound, quoteRequest)
}

export const trackBookingCreated = (
  quotedBooking: any,
  bookingResponseData: any,
) => {
  const selections = getBookingSelections(quotedBooking)
  if (selections.length === 0) {
    return
  }

  const selectedVehicleTypes = sanitizeString(
    [
      ...new Set(
        selections
          .map(
            (selection) =>
              findStoredQuoteOption(
                selection.quoteReference,
                selection.supplierQuoteReference,
              )?.vehicleTypeName,
          )
          .filter(Boolean),
      ),
    ].join('|'),
  )

  trackEvent('booking_created', {
    booking_reference: bookingResponseData?.bookingReference,
    journey_reference: bookingResponseData?.journeyReference,
    selected_quote_count: selections.length,
    selected_vehicle_count: selections.reduce(
      (total, selection) => total + selection.quantity,
      0,
    ),
    selected_vehicle_types: selectedVehicleTypes,
  })

  selections.forEach((selection, index) => {
    const selectedQuote = findStoredQuoteOption(
      selection.quoteReference,
      selection.supplierQuoteReference,
    )

    trackEvent('quote_option_booked', {
      booking_reference: bookingResponseData?.bookingReference,
      journey_leg: selection.journeyLeg,
      option_index: index + 1,
      quote_reference: selection.quoteReference,
      supplier_quote_reference: selection.supplierQuoteReference,
      vehicle_type: selectedQuote?.vehicleTypeName,
      supplier_name: selectedQuote?.supplierName,
      quantity: selection.quantity,
      price_total: selectedQuote?.priceTotal,
      currency: selectedQuote?.currency,
    })
  })
}
